import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import type { ForwardRefRenderFunction, MutableRefObject, PropsWithoutRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { LeafletProvider } from './context';
import type { DivOverlay, DivOverlayHook } from './div-overlay';
import type { LeafletElement } from './element';

type ElementHook<E, P> = (props: P) => MutableRefObject<LeafletElement<E>>;
export type PropsWithChildren = { children?: ReactNode };

export function createContainerComponent<E, P extends PropsWithChildren>(useElement: ElementHook<E, P>) {
    const ContainerComponent: ForwardRefRenderFunction<E, P> = function ContainerComponent(props, forwardedRef) {
        const { instance , context  } = useElement(props).current;
        useImperativeHandle(forwardedRef, ()=>instance);
        return props.children == null ? null : /*#__PURE__*/ React.createElement(LeafletProvider, {
            value: context
        }, props.children);
    };
    return /*#__PURE__*/ forwardRef<E, P>(ContainerComponent as ForwardRefRenderFunction<E, PropsWithoutRef<P>>);
}
export function createDivOverlayComponent<E extends DivOverlay, P extends PropsWithChildren>(useElement: ReturnType<DivOverlayHook<E, P>>) {
    const OverlayComponent: ForwardRefRenderFunction<E, P> = function OverlayComponent(props, forwardedRef) {
        const [isOpen, setOpen] = useState(false);
        const { instance  } = useElement(props, setOpen).current;
        useImperativeHandle(forwardedRef, ()=>instance);
        useEffect(function updateOverlay() {
            if (isOpen) {
                instance.update();
            }
        }, [
            instance,
            isOpen,
            props.children
        ]);
        const contentNode = instance._contentNode;
        return contentNode ? /*#__PURE__*/ createPortal(props.children, contentNode) : null;
    };
    return /*#__PURE__*/ forwardRef<E, P>(OverlayComponent as ForwardRefRenderFunction<E, PropsWithoutRef<P>>);
}
export function createLeafComponent<E, P>(useElement: ElementHook<E, P>) {
    const LeafComponent: ForwardRefRenderFunction<E, P> = function LeafComponent(props, forwardedRef) {
        const { instance  } = useElement(props).current;
        useImperativeHandle(forwardedRef, ()=>instance);
        return null;
    };
    return /*#__PURE__*/ forwardRef<E, P>(LeafComponent as ForwardRefRenderFunction<E, PropsWithoutRef<P>>);
}
